import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { STRIPE_PAYMENTS_ENABLED } from '../featureFlags';

// ── Constants ─────────────────────────────────────────────────────────────────

const STEPS = ['Language', 'Terms', 'Payments', 'Contact', 'Preview', 'Signature'];

const PAYMENT_PLANS = ['Weekly', 'Bi-Weekly', 'Monthly'];

const DURATION_OPTIONS = [
    { days: 30,  label: '30 days / 30 días' },
    { days: 60,  label: '60 days / 60 días' },
    { days: 90,  label: '90 days / 90 días' },
    { days: 0,   label: 'Custom / Personalizado' },
];

const EXCHANGE_DAY_OPTIONS = [7, 14, 21, 30];

const fmt = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
        .format(Number(amount ?? 0));

const fmtDate = (isoStr, locale = 'en-US') => {
    if (!isoStr) return 'N/A';
    return new Date(isoStr).toLocaleDateString(locale, {
        year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
    });
};

/**
 * Advance a date to the next Fri / Sat / Sun (store business days).
 * Mon–Thu get bumped to the following Friday.
 */
function snapToStoreDay(isoStr) {
    if (!isoStr) return isoStr;
    const d = new Date(isoStr);
    const dow = d.getUTCDay(); // 0=Sun 1=Mon 2=Tue 3=Wed 4=Thu 5=Fri 6=Sat
    if (dow === 0 || dow === 5 || dow === 6) return isoStr; // already a store day
    // Mon(1)→+4 Tue(2)→+3 Wed(3)→+2 Thu(4)→+1
    const advance = [0, 4, 3, 2, 1, 0, 0][dow];
    d.setUTCDate(d.getUTCDate() + advance);
    return d.toISOString();
}

function addDays(days) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + days);
    d.setUTCHours(0, 0, 0, 0);
    return snapToStoreDay(d.toISOString());
}

/** Auto-calculate installment schedule. */
function calcInstallment(total, deposit, plan, durationDays) {
    const remaining = Math.max(0, (total ?? 0) - parseFloat(deposit || 0));
    if (remaining <= 0 || durationDays <= 0) return { amount: 0, payments: 0, remaining: 0 };
    const daysPerPmt = plan === 'Weekly' ? 7 : plan === 'Bi-Weekly' ? 14 : 30;
    const payments   = Math.max(1, Math.floor(durationDays / daysPerPmt));
    const amount     = Math.ceil((remaining / payments) * 100) / 100;
    return { amount, payments, remaining };
}

function today(locale = 'en-US') {
    return new Date().toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
}

// ── Contract copy (all static text, both languages) ───────────────────────────

const COPY = {
    en: {
        title:   'LAYAWAY AGREEMENT',
        company: 'The Jewelry Doctors of Auburndale',
        address: 'Auburndale, FL  |  (863) 967-6699',

        s1: '1. CUSTOMER & TRANSACTION DETAILS',
        s2: '2. LAYAWAY PAYMENT SCHEDULE',
        s3: '3. PAYMENT METHODS & PROCESSING',
        s4: '4. GRACE PERIOD, MATURITY, AND DEFAULT POLICY',
        s5: '5. MERCHANDISE EXCHANGE RULES',
        s6: '6. CUSTOMER ACKNOWLEDGMENT & AUTHORIZATION',

        l: {
            contractDate: 'Contract Date',
            ticket:       'Ticket #',
            employee:     'Store Representative',
            customer:     'Customer Name',
            phone:        'Phone',
            email:        'Email',
            items:        'Item(s)',
            qty:          'Qty',
            description:  'Description',
            price:        'Price',
            total:        'Transaction Total',
            deposit:      'Deposit Paid Today',
            balance:      'Remaining Balance',
            plan:         'Payment Plan',
            installment:  'Amount per Payment',
            numPayments:  'Estimated Payments',
            duration:     'Layaway Duration',
            maturity:     'Maturity Date',
            days:         'days',
        },

        s3body: [
            'In-Store Payments: Cash, debit, or credit card payments made directly at The Jewelry Doctors of Auburndale are accepted and will be applied immediately to the layaway balance.',
            'Stripe (Online/Card): Payments processed through Stripe will be applied to the layaway balance upon successful authorization. The Jewelry Doctors is not responsible for delays caused by third-party processors.',
            'Zelle: Zelle transfers must be sent to the store\'s registered Zelle account and confirmed by a store representative. A confirmation number must be obtained. Transfers without confirmation will not be credited.',
            'Ownership Transfer: Title to the merchandise does not transfer to the customer until the layaway balance is paid in full and the item is physically retrieved from the store.',
        ],

        s4body: [
            'Grace Period: The customer has a 5-day grace period after each scheduled payment due date before the account is considered in default.',
            'Maturity: The layaway must be paid in full by the Maturity Date. Failure to do so will result in the account entering default status.',
            'Default: An account in default is subject to cancellation. Upon cancellation, all deposits and payments made are forfeited and will not be refunded.',
            'No Refunds: All payments made toward a layaway are non-refundable. In the event of cancellation, no portion of the payments will be returned.',
            'Forfeiture: Merchandise held under a cancelled layaway agreement will be returned to general inventory and may be sold to other customers.',
        ],

        s5standard: (n) =>
            `Standard Merchandise Exchange: After the Maturity Date, the customer has ${n} day(s) to exchange the merchandise for items of equal or greater value. No cash refunds will be issued. Exchanges are subject to merchandise availability.`,

        s5custom:
            'Custom & Sized Jewelry — Final Sale: The item(s) in this layaway agreement include custom-made or specifically sized jewelry. These items are FINAL SALE. No exchanges, returns, or refunds will be accepted under any circumstances. By signing this agreement, the customer acknowledges that this merchandise cannot be re-sold to another customer.',

        s6statute:
            'Florida Statute § 501.142 — Layaway Sales: Pursuant to Florida law, this layaway agreement is subject to the provisions of § 501.142, Florida Statutes. The seller must hold merchandise for the agreed layaway period. If the seller cancels this agreement without cause, the customer is entitled to a full refund of all deposits and payments made.',

        s6consent:
            'By signing below, I, the customer, confirm that: (1) I have read and fully understand all terms and conditions of this Layaway Agreement; (2) I agree to make payments as scheduled; (3) I understand that failure to complete payments will result in forfeiture of all deposits; (4) I acknowledge the exchange/final-sale policy for my specific merchandise; and (5) I authorize The Jewelry Doctors of Auburndale to hold the listed merchandise on layaway under the terms stated herein.',

        sigCustomer: 'Customer Signature',
        sigDate:     'Date',
        sigRep:      'Store Representative',
        printName:   'Print Name',
    },

    es: {
        title:   'CONTRATO DE APARTADO',
        company: 'The Jewelry Doctors of Auburndale',
        address: 'Auburndale, FL  |  (863) 967-6699',

        s1: '1. DATOS DEL CLIENTE Y LA TRANSACCIÓN',
        s2: '2. PLAN DE PAGOS DEL APARTADO',
        s3: '3. MÉTODOS DE PAGO Y PROCESAMIENTO',
        s4: '4. PERÍODO DE GRACIA, VENCIMIENTO Y POLÍTICA DE INCUMPLIMIENTO',
        s5: '5. REGLAS DE CAMBIO DE MERCANCÍA',
        s6: '6. RECONOCIMIENTO Y AUTORIZACIÓN DEL CLIENTE',

        l: {
            contractDate: 'Fecha del Contrato',
            ticket:       'Ticket #',
            employee:     'Representante de la Tienda',
            customer:     'Nombre del Cliente',
            phone:        'Teléfono',
            email:        'Correo Electrónico',
            items:        'Artículo(s)',
            qty:          'Cant.',
            description:  'Descripción',
            price:        'Precio',
            total:        'Total de la Transacción',
            deposit:      'Depósito Pagado Hoy',
            balance:      'Saldo Restante',
            plan:         'Plan de Pago',
            installment:  'Monto por Cuota',
            numPayments:  'Pagos Estimados',
            duration:     'Duración del Apartado',
            maturity:     'Fecha de Vencimiento',
            days:         'días',
        },

        s3body: [
            'Pagos en Tienda: Se aceptan pagos en efectivo, débito o tarjeta de crédito directamente en The Jewelry Doctors of Auburndale y se aplicarán de inmediato al saldo del apartado.',
            'Stripe (En línea / Tarjeta): Los pagos procesados mediante Stripe se aplicarán al saldo del apartado una vez autorizado exitosamente. The Jewelry Doctors no es responsable por demoras causadas por procesadores externos.',
            'Zelle: Las transferencias por Zelle deben enviarse a la cuenta Zelle registrada de la tienda y ser confirmadas por un representante. Se debe obtener un número de confirmación. Las transferencias sin confirmación no serán acreditadas.',
            'Transferencia de Propiedad: El título de la mercancía no pasa al cliente hasta que el saldo del apartado esté pagado en su totalidad y el artículo sea retirado físicamente de la tienda.',
        ],

        s4body: [
            'Período de Gracia: El cliente tiene 5 días de gracia después de cada fecha de pago programada antes de que la cuenta sea considerada en mora.',
            'Vencimiento: El apartado debe estar pagado en su totalidad antes de la Fecha de Vencimiento. El incumplimiento resultará en que la cuenta entre en estado de mora.',
            'Incumplimiento: Una cuenta en mora está sujeta a cancelación. Al cancelarse, todos los depósitos y pagos realizados se pierden y no serán reembolsados.',
            'Sin Reembolsos: Todos los pagos realizados hacia un apartado son no reembolsables. En caso de cancelación, no se devolverá ninguna parte de los pagos.',
            'Confiscación: La mercancía retenida bajo un contrato de apartado cancelado será devuelta al inventario general y podrá venderse a otros clientes.',
        ],

        s5standard: (n) =>
            `Cambio de Mercancía Estándar: Después de la Fecha de Vencimiento, el cliente tiene ${n} día(s) para cambiar la mercancía por artículos de igual o mayor valor. No se emitirán reembolsos en efectivo. Los cambios están sujetos a disponibilidad de mercancía.`,

        s5custom:
            'Joyería Personalizada y de Talla Específica — Venta Final: Los artículo(s) de este contrato incluyen joyería hecha a medida o de talla específica. Estos artículos son VENTA FINAL. No se aceptarán cambios, devoluciones ni reembolsos bajo ninguna circunstancia. Al firmar este contrato, el cliente reconoce que esta mercancía no puede ser revendida a otro cliente.',

        s6statute:
            'Estatuto de Florida § 501.142 — Ventas en Apartado: De conformidad con la ley de Florida, este contrato de apartado está sujeto a las disposiciones del § 501.142 de los Estatutos de Florida. El vendedor debe conservar la mercancía durante el período de apartado acordado. Si el vendedor cancela este contrato sin causa justificada, el cliente tiene derecho a un reembolso completo de todos los depósitos y pagos realizados.',

        s6consent:
            'Al firmar a continuación, yo, el cliente, confirmo que: (1) He leído y entiendo completamente todos los términos y condiciones de este Contrato de Apartado; (2) Acepto realizar los pagos según lo programado; (3) Entiendo que el incumplimiento de pagos resultará en la pérdida de todos los depósitos; (4) Reconozco la política de cambio/venta final para mi mercancía específica; y (5) Autorizo a The Jewelry Doctors of Auburndale a retener la mercancía listada en apartado bajo los términos aquí establecidos.',

        sigCustomer: 'Firma del Cliente',
        sigDate:     'Fecha',
        sigRep:      'Representante de la Tienda',
        printName:   'Nombre en Letra de Imprenta',
    },
};

// ── ContractPreview ───────────────────────────────────────────────────────────

function ContractPreview({
    lang, sale, lineItems, depositAmount, installmentAmount,
    paymentPlan, durationDays, maturityDate, exchangeDays,
    isCustomJewelry, allowOnlinePayments, passCcFees,
    employeeName, customerPhone, customerEmail,
}) {
    const c      = COPY[lang] ?? COPY.en;
    const locale = lang === 'es' ? 'es-US' : 'en-US';
    const total  = sale?.transactionTotal ?? 0;
    const balance = Math.max(0, total - parseFloat(depositAmount || 0));
    const { payments } = calcInstallment(total, depositAmount, paymentPlan, durationDays);
    const todayStr = today(locale);

    const hdr = {
        fontWeight: 'bold',
        borderBottom: '1px solid #bbb',
        paddingBottom: 3,
        marginBottom: 8,
        marginTop: 16,
        fontSize: 11,
        letterSpacing: 0.5,
        color: '#1a1a1a',
        textTransform: 'uppercase',
    };
    const row   = { display: 'flex', marginBottom: 4, fontSize: 11 };
    const lbl   = { fontWeight: '600', minWidth: 200, color: '#444', flexShrink: 0 };
    const bodyP = { fontSize: 10.5, lineHeight: 1.65, color: '#333', margin: '0 0 6px' };

    return (
        <div style={{
            fontFamily: '"Georgia", serif',
            fontSize: 11,
            color: '#111',
            maxHeight: 520,
            overflowY: 'auto',
            border: '2px solid #c8a96e',
            borderRadius: 6,
            padding: '20px 28px',
            background: '#fffdf8',
            lineHeight: 1.6,
        }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 16, fontWeight: 'bold' }}>{c.company}</div>
                <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>{c.address}</div>
                <div style={{ height: 1.5, background: '#c8a96e', margin: '8px 0' }} />
                <div style={{ fontSize: 14, fontWeight: 'bold', letterSpacing: 1.5 }}>{c.title}</div>
                <div style={{ fontSize: 10, color: '#555', marginTop: 3 }}>
                    {c.l.contractDate}: {todayStr}
                    {sale?.ticketId && <span>  —  {c.l.ticket} {sale.ticketId}</span>}
                </div>
            </div>

            {/* ── S1: Customer & Transaction Details ── */}
            <div style={hdr}>{c.s1}</div>
            <div style={row}><span style={lbl}>{c.l.customer}:</span><span>{sale?.customerName ?? '—'}</span></div>
            {customerPhone && <div style={row}><span style={lbl}>{c.l.phone}:</span><span>{customerPhone}</span></div>}
            {customerEmail && <div style={row}><span style={lbl}>{c.l.email}:</span><span>{customerEmail}</span></div>}
            <div style={row}><span style={lbl}>{c.l.employee}:</span><span>{employeeName ?? '—'}</span></div>

            {/* Line items table */}
            {lineItems && lineItems.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 10 }}>
                    <thead>
                        <tr style={{ background: '#f0e8d8', borderBottom: '1px solid #c8a96e' }}>
                            <th style={{ padding: '3px 6px', textAlign: 'left' }}>#</th>
                            <th style={{ padding: '3px 6px', textAlign: 'left' }}>{c.l.description}</th>
                            <th style={{ padding: '3px 6px', textAlign: 'center' }}>{c.l.qty}</th>
                            <th style={{ padding: '3px 6px', textAlign: 'right' }}>{c.l.price}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lineItems.map((item, i) => (
                            <tr key={item.id ?? i} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '3px 6px', color: '#888' }}>{i + 1}</td>
                                <td style={{ padding: '3px 6px' }}>
                                    {item.title || item.description || '—'}
                                    {item.material && <span style={{ color: '#888' }}> ({item.material})</span>}
                                </td>
                                <td style={{ padding: '3px 6px', textAlign: 'center' }}>{item.quantity ?? 1}</td>
                                <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmt(item.cost)}</td>
                            </tr>
                        ))}
                        <tr style={{ fontWeight: 'bold', borderTop: '2px solid #c8a96e' }}>
                            <td colSpan={3} style={{ padding: '4px 6px', textAlign: 'right' }}>{c.l.total}:</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmt(total)}</td>
                        </tr>
                    </tbody>
                </table>
            )}
            {(!lineItems || lineItems.length === 0) && (
                <div style={row}><span style={lbl}>{c.l.total}:</span><span>{fmt(total)}</span></div>
            )}

            {/* ── S2: Payment Schedule ── */}
            <div style={hdr}>{c.s2}</div>
            <div style={row}><span style={lbl}>{c.l.deposit}:</span><span style={{ color: '#0a7c3e', fontWeight: 600 }}>{fmt(depositAmount)}</span></div>
            <div style={row}><span style={lbl}>{c.l.balance}:</span><span style={{ color: '#c0392b', fontWeight: 600 }}>{fmt(balance)}</span></div>
            <div style={row}><span style={lbl}>{c.l.plan}:</span><span>{paymentPlan}</span></div>
            <div style={row}><span style={lbl}>{c.l.installment}:</span><span style={{ fontWeight: 600 }}>{fmt(installmentAmount)}</span></div>
            <div style={row}><span style={lbl}>{c.l.numPayments}:</span><span>{payments}</span></div>
            <div style={row}><span style={lbl}>{c.l.duration}:</span><span>{durationDays} {c.l.days}</span></div>
            <div style={row}><span style={lbl}>{c.l.maturity}:</span><span style={{ fontWeight: 600 }}>{fmtDate(maturityDate, locale)}</span></div>

            {/* ── S3: Payment Methods ── */}
            <div style={hdr}>{c.s3}</div>
            <p style={bodyP}>• {passCcFees
                ? (lang === 'es'
                    ? 'Pagos en Tienda: Efectivo aceptado sin cargo adicional. Los pagos con tarjeta llevan un cargo de plataforma del 3%.'
                    : 'In-Store Payments: Cash accepted at no extra charge. Card payments carry a 3% platform fee.')
                : (lang === 'es'
                    ? 'Pagos en Tienda: Se aceptan efectivo o tarjeta de crédito/débito directamente en la tienda.'
                    : 'In-Store Payments: Cash or card payments are accepted directly at the store.')
            }</p>
            <p style={bodyP}>• {allowOnlinePayments
                ? (passCcFees
                    ? (lang === 'es'
                        ? 'Pagos en Línea (Stripe): HABILITADOS. Se aplicará un cargo de plataforma del 3% a todos los pagos en línea. Los pagos en tienda siguen disponibles sin cargo adicional.'
                        : 'Online Payments (Stripe): ENABLED. A 3% online platform fee applies to all online payments to cover remote digital payment processing. In-store payments remain available at no additional charge.')
                    : (lang === 'es'
                        ? 'Pagos en Línea (Stripe): HABILITADOS. El cliente puede realizar pagos de cuotas en línea.'
                        : 'Online Payments (Stripe): ENABLED. Customer may make installment payments online.'))
                : (lang === 'es'
                    ? 'Pagos en Línea: NO habilitados. Todos los pagos deben realizarse en persona.'
                    : 'Online Payments: NOT enabled. All payments must be made in person at the store.')
            }</p>
            {c.s3body.slice(2).map((p, i) => (
                <p key={i} style={bodyP}>• {p}</p>
            ))}

            {/* ── S4: Grace Period / Default ── */}
            <div style={hdr}>{c.s4}</div>
            {c.s4body.map((p, i) => (
                <p key={i} style={bodyP}>• {p}</p>
            ))}

            {/* ── S5: Exchange Rules ── */}
            <div style={hdr}>{c.s5}</div>
            {isCustomJewelry
                ? <p style={{ ...bodyP, fontWeight: 'bold', color: '#8b0000' }}>{c.s5custom}</p>
                : <p style={bodyP}>{c.s5standard(exchangeDays)}</p>
            }

            {/* ── S6: Acknowledgment ── */}
            <div style={hdr}>{c.s6}</div>
            <p style={{ ...bodyP, fontSize: 10, fontStyle: 'italic', borderLeft: '3px solid #c8a96e', paddingLeft: 8 }}>
                {c.s6statute}
            </p>
            <p style={bodyP}>{c.s6consent}</p>

            {/* Signature block */}
            <div style={{ display: 'flex', gap: 40, marginTop: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ borderBottom: '1px solid #333', marginBottom: 3, height: 32 }} />
                    <div style={{ fontSize: 10, color: '#555' }}>{c.sigCustomer}</div>
                    <div style={{ fontSize: 10, color: '#555', marginTop: 4 }}>{c.sigDate}: {todayStr}</div>
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ borderBottom: '1px solid #333', marginBottom: 3, height: 32, fontSize: 10, color: '#333', paddingTop: 14, fontStyle: 'italic' }}>
                        {employeeName}
                    </div>
                    <div style={{ fontSize: 10, color: '#555' }}>{c.sigRep}</div>
                    <div style={{ fontSize: 10, color: '#555', marginTop: 4 }}>{c.sigDate}: {todayStr}</div>
                </div>
            </div>
        </div>
    );
}

// ── Main Wizard ───────────────────────────────────────────────────────────────

export function LayawayWizard({ show, onClose, onSubmit, sale, customer, lineItems, employeeName }) {
    const { t } = useTranslation();
    const canvasRef    = useRef(null);
    const isDrawingRef = useRef(false);

    // Wizard state
    const [step, setStep]               = useState(0);
    const [submitting, setSubmitting]   = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [hasSigned, setHasSigned]     = useState(false);

    // Step 0 — Language
    const [language, setLanguage] = useState('en');

    // Step 1 — Terms
    const [durationDays, setDurationDays]             = useState(90);
    const [customDuration, setCustomDuration]         = useState('');
    const [durationPreset, setDurationPreset]         = useState(90);
    const [maturityDate, setMaturityDate]             = useState(addDays(90));
    const [isCustomJewelry, setIsCustomJewelry]       = useState(false);
    const [exchangeDays, setExchangeDays]             = useState(30);
    const [allowOnlinePayments, setAllowOnlinePayments] = useState(false);
    const [passCcFees, setPassCcFees]                 = useState(false);

    // Step 2 — Payments
    const [depositAmount,        setDepositAmount]        = useState('');
    const [depositPaymentMethod, setDepositPaymentMethod] = useState('Cash');
    const [paymentPlan,          setPaymentPlan]          = useState('Monthly');
    const [installmentAmount,    setInstallmentAmount]    = useState('');
    const [installmentLocked,    setInstallmentLocked]    = useState(false);

    // Step 3 — Contact
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');

    // Convenience
    const total  = sale?.transactionTotal ?? 0;
    const locale = language === 'es' ? 'es-US' : 'en-US';

    // ── Auto-calculations ─────────────────────────────────────────────────────

    const calc = calcInstallment(total, depositAmount, paymentPlan, durationDays);

    useEffect(() => {
        if (!installmentLocked && calc.amount > 0) {
            setInstallmentAmount(String(calc.amount));
        }
    }, [depositAmount, paymentPlan, durationDays, installmentLocked, calc.amount]);

    // Sync maturity date when duration changes (snap to store day)
    useEffect(() => {
        if (durationDays > 0) setMaturityDate(addDays(durationDays));
    }, [durationDays]);

    // Prefill contact from customer
    useEffect(() => {
        if (customer) {
            setCustomerPhone(customer.phoneNumber || customer.phone || '');
            setCustomerEmail(customer.email || '');
        }
    }, [customer]);

    // Reset on open
    useEffect(() => {
        if (show) {
            setStep(0);
            setSubmitError(null);
            setLanguage('en');
            setDurationPreset(90);
            setDurationDays(90);
            setCustomDuration('');
            setIsCustomJewelry(false);
            setExchangeDays(30);
            setAllowOnlinePayments(false);
            setPassCcFees(false);
            setDepositAmount('');
            setDepositPaymentMethod('Cash');
            setPaymentPlan('Monthly');
            setInstallmentAmount('');
            setInstallmentLocked(false);
            setSubmitting(false);
            setHasSigned(false);
            setTimeout(clearCanvas, 50);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    // ── Canvas helpers ────────────────────────────────────────────────────────

    const getPos = (e, canvas) => {
        const rect = canvas.getBoundingClientRect();
        const src  = e.touches ? e.touches[0] : e;
        return { x: src.clientX - rect.left, y: src.clientY - rect.top };
    };
    const startDraw = (e) => {
        const canvas = canvasRef.current; if (!canvas) return;
        isDrawingRef.current = true;
        const ctx = canvas.getContext('2d');
        const pos = getPos(e, canvas);
        ctx.beginPath(); ctx.moveTo(pos.x, pos.y);
        e.preventDefault();
    };
    const draw = (e) => {
        if (!isDrawingRef.current) return;
        const canvas = canvasRef.current; if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const pos = getPos(e, canvas);
        ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#000';
        ctx.lineTo(pos.x, pos.y); ctx.stroke();
        e.preventDefault();
    };
    const stopDraw = () => {
        isDrawingRef.current = false;
        // Check after the stroke is committed whether anything was drawn
        setHasSigned(!isCanvasEmpty());
    };
    const clearCanvas = () => {
        const canvas = canvasRef.current; if (!canvas) return;
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        setHasSigned(false);
    };
    const getSignatureBase64 = () => canvasRef.current?.toDataURL('image/png') ?? '';
    const isCanvasEmpty = () => {
        const canvas = canvasRef.current; if (!canvas) return true;
        return !canvas.getContext('2d')
            .getImageData(0, 0, canvas.width, canvas.height).data.some((v) => v !== 0);
    };

    // ── Validation ────────────────────────────────────────────────────────────

    const stepValid = useCallback(() => {
        switch (step) {
            case 0: return !!language;
            case 1: return durationDays > 0 && !!maturityDate;
            case 2: return parseFloat(depositAmount) > 0 && parseFloat(installmentAmount) > 0 && !!paymentPlan;
            case 3: return true; // contact optional
            case 4: return true; // preview — always valid
            case 5: return hasSigned;
            default: return true;
        }
    }, [step, language, durationDays, maturityDate, depositAmount, installmentAmount, paymentPlan, hasSigned]);

    // ── Submit ────────────────────────────────────────────────────────────────

    const handleSubmit = async () => {
        if (isCanvasEmpty()) {
            setSubmitError(language === 'es'
                ? 'Por favor proporcione una firma antes de enviar.'
                : 'Please provide a signature before submitting.');
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        try {
            await onSubmit({
                depositAmount:        parseFloat(depositAmount),
                depositPaymentMethod,
                installmentAmount:    parseFloat(installmentAmount),
                paymentPlan,
                durationDays,
                maturityDate,
                exchangeDays,
                isCustomJewelry,
                allowOnlinePayments,
                passCcFees,
                signatureBase64:      getSignatureBase64(),
                customerPhone,
                customerEmail,
                language,
                lineItems: (lineItems || []).map((li) => ({
                    title:       li.title || li.description || '',
                    description: li.description || '',
                    quantity:    li.quantity ?? 1,
                    cost:        li.cost ?? 0,
                    material:    li.material || '',
                })),
            });
        } catch (e) {
            setSubmitError(e?.response?.data || e?.message || t('Failed to generate contract'));
            setSubmitting(false);
        }
    };

    // ── Step labels ───────────────────────────────────────────────────────────

    const STEP_LABELS = {
        en: ['Language', 'Terms', 'Payments', 'Contact', 'Preview', 'Signature'],
        es: ['Idioma', 'Términos', 'Pagos', 'Contacto', 'Vista Previa', 'Firma'],
    };
    const stepLabel = (STEP_LABELS[language] ?? STEP_LABELS.en)[step];

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Modal show={show} onHide={onClose} size="lg" backdrop="static" scrollable>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    {language === 'es' ? 'Contrato de Apartado' : 'Layaway Contract'}
                    <span className="text-muted fs-6 fw-normal">
                        — {language === 'es' ? 'Paso' : 'Step'} {step + 1}/{STEPS.length}:
                    </span>
                    <Badge bg="secondary">{stepLabel}</Badge>
                </Modal.Title>
            </Modal.Header>

            {/* Progress bar */}
            <div style={{ height: 4, background: '#e9ecef' }}>
                <div style={{
                    height: '100%',
                    width: `${((step + 1) / STEPS.length) * 100}%`,
                    background: '#0d6efd',
                    transition: 'width 0.3s ease',
                }} />
            </div>

            <Modal.Body>
                {submitError && (
                    <Alert variant="danger" dismissible onClose={() => setSubmitError(null)}>
                        {submitError}
                    </Alert>
                )}

                {/* ── Step 0: Language ── */}
                {step === 0 && (
                    <div className="text-center py-3">
                        <h5 className="mb-1">Select Contract Language</h5>
                        <p className="text-muted small mb-4">Seleccione el idioma del contrato</p>
                        <div className="d-flex justify-content-center gap-4">
                            {[
                                { code: 'en', flag: '🇺🇸', name: 'English', sub: 'English contract' },
                                { code: 'es', flag: '🇵🇷', name: 'Español', sub: 'Contrato en español' },
                            ].map(({ code, flag, name, sub }) => (
                                <div
                                    key={code}
                                    onClick={() => setLanguage(code)}
                                    style={{
                                        cursor: 'pointer',
                                        border: `3px solid ${language === code ? '#0d6efd' : '#dee2e6'}`,
                                        borderRadius: 12,
                                        padding: '24px 36px',
                                        background: language === code ? '#e8f0fe' : '#fff',
                                        transition: 'all 0.2s',
                                        minWidth: 160,
                                    }}
                                >
                                    <div style={{ fontSize: 48, lineHeight: 1 }}>{flag}</div>
                                    <div style={{ fontWeight: 700, marginTop: 10, fontSize: 16 }}>{name}</div>
                                    <div className="text-muted small">{sub}</div>
                                    {language === code && (
                                        <Badge bg="primary" className="mt-2">
                                            {code === 'en' ? '✓ Selected' : '✓ Seleccionado'}
                                        </Badge>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Step 1: Terms ── */}
                {step === 1 && (
                    <>
                        <h5>{language === 'es' ? 'Duración y Política' : 'Duration & Policy'}</h5>

                        {/* Duration preset */}
                        <Form.Group className="mb-3">
                            <Form.Label><strong>{language === 'es' ? 'Duración del Apartado' : 'Layaway Duration'}</strong></Form.Label>
                            <Form.Select
                                value={durationPreset}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setDurationPreset(val);
                                    if (val > 0) setDurationDays(val);
                                    else setDurationDays(0);
                                }}
                            >
                                {DURATION_OPTIONS.map((o) => (
                                    <option key={o.days} value={o.days}>{o.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {/* Custom duration input */}
                        {durationPreset === 0 && (
                            <Form.Group className="mb-3">
                                <Form.Label><strong>{language === 'es' ? 'Días personalizados' : 'Custom days'}</strong></Form.Label>
                                <Form.Control
                                    type="number" min={1} max={365}
                                    value={customDuration}
                                    onChange={(e) => {
                                        setCustomDuration(e.target.value);
                                        const v = parseInt(e.target.value, 10);
                                        if (v > 0) setDurationDays(v);
                                    }}
                                    placeholder="e.g. 45"
                                />
                            </Form.Group>
                        )}

                        {/* Maturity date (snapped to Fri/Sat/Sun) */}
                        <Form.Group className="mb-3">
                            <Form.Label><strong>{language === 'es' ? 'Fecha de Vencimiento' : 'Maturity Date'}</strong></Form.Label>
                            <Form.Control
                                type="date"
                                value={maturityDate ? maturityDate.split('T')[0] : ''}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        const snapped = snapToStoreDay(new Date(e.target.value + 'T00:00:00Z').toISOString());
                                        setMaturityDate(snapped);
                                    }
                                }}
                            />
                            <Form.Text className="text-muted">
                                {language === 'es'
                                    ? 'La fecha se ajusta automáticamente al próximo Viernes, Sábado o Domingo (días de tienda).'
                                    : 'Date snaps to the next Fri / Sat / Sun (store business days).'}
                            </Form.Text>
                        </Form.Group>

                        {/* Custom / Sized Jewelry toggle */}
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                id="custom-jewelry-switch"
                                label={
                                    <strong>
                                        {language === 'es'
                                            ? 'Joyería personalizada o de talla específica (Venta Final)'
                                            : 'Custom or sized jewelry (Final Sale — no exchange)'}
                                    </strong>
                                }
                                checked={isCustomJewelry}
                                onChange={(e) => setIsCustomJewelry(e.target.checked)}
                            />
                            <Form.Text className="text-muted">
                                {language === 'es'
                                    ? 'Active esto si el artículo es hecho a medida o de talla personalizada. No se permiten cambios.'
                                    : 'Enable if the item is custom-made or sized. No exchanges allowed.'}
                            </Form.Text>
                        </Form.Group>

                        {/* Exchange days — only when NOT custom jewelry */}
                        {!isCustomJewelry && (
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <strong>
                                        {language === 'es'
                                            ? 'Días de Gracia para Cambio (después del vencimiento)'
                                            : 'Exchange Grace Period (days after maturity)'}
                                    </strong>
                                </Form.Label>
                                <Form.Select
                                    value={exchangeDays}
                                    onChange={(e) => setExchangeDays(Number(e.target.value))}
                                >
                                    {EXCHANGE_DAY_OPTIONS.map((d) => (
                                        <option key={d} value={d}>{d} {language === 'es' ? 'días' : 'days'}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}

                        {isCustomJewelry && (
                            <Alert variant="warning" className="small">
                                ⚠️ {language === 'es'
                                    ? 'Venta Final: No se permitirán cambios ni reembolsos. Esto quedará indicado en el contrato.'
                                    : 'Final Sale: No exchanges or refunds will be accepted. This will be noted in the contract.'}
                            </Alert>
                        )}

                        <hr className="my-3" />

                        {/* Online payments toggle — hidden when Stripe is disabled */}
                        {STRIPE_PAYMENTS_ENABLED && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="online-payments-switch"
                                        label={
                                            <strong>
                                                {language === 'es'
                                                    ? 'Permitir pagos en línea (Stripe)'
                                                    : 'Allow online payments (Stripe)'}
                                            </strong>
                                        }
                                        checked={allowOnlinePayments}
                                        onChange={(e) => setAllowOnlinePayments(e.target.checked)}
                                    />
                                    <Form.Text className="text-muted">
                                        {language === 'es'
                                            ? 'El cliente podrá realizar pagos en línea con tarjeta de crédito/débito.'
                                            : 'Customer will be able to make installment payments online via card.'}
                                    </Form.Text>
                                </Form.Group>

                                {/* CC fee passthrough toggle */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="cc-fees-switch"
                                        label={
                                            <strong>
                                                {language === 'es'
                                                    ? 'Cobrar cargo de plataforma al cliente (3%)'
                                                    : 'Pass online platform fee to customer (3%)'}
                                            </strong>
                                        }
                                        checked={passCcFees}
                                        onChange={(e) => setPassCcFees(e.target.checked)}
                                    />
                                    <Form.Text className="text-muted">
                                        {language === 'es'
                                            ? 'Se aplicará un cargo de plataforma del 3% a todos los pagos en línea. Los pagos en tienda siempre están disponibles sin este cargo.'
                                            : 'A 3% platform fee will be added to all online payments. In-store payments are always available without this fee.'}
                                    </Form.Text>
                                </Form.Group>

                                {passCcFees && (
                                    <Alert variant="info" className="small mb-0">
                                        💳 {language === 'es'
                                            ? 'Este cargo quedará indicado en el contrato. El cliente debe aceptarlo antes de firmar.'
                                            : 'This fee will be disclosed in the contract. The customer must acknowledge it before signing.'}
                                    </Alert>
                                )}
                            </>
                        )}
                    </>
                )}

                {/* ── Step 2: Payments ── */}
                {step === 2 && (
                    <>
                        <h5>{language === 'es' ? 'Detalles de Pago' : 'Payment Details'}</h5>

                        <div className="alert alert-light border mb-3 p-2 small">
                            <strong>{language === 'es' ? 'Total de la transacción' : 'Transaction total'}:</strong>{' '}
                            <span className="fw-bold">{fmt(total)}</span>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label><strong>{language === 'es' ? 'Depósito Pagado Hoy' : 'Deposit Amount (paid today)'}</strong></Form.Label>
                            <Form.Control
                                type="number" min={0.01} step={0.01}
                                value={depositAmount}
                                onChange={(e) => { setDepositAmount(e.target.value); setInstallmentLocked(false); }}
                                placeholder="0.00"
                            />
                            {parseFloat(depositAmount) > 0 && (
                                <Form.Text className="text-muted">
                                    {language === 'es' ? 'Saldo restante' : 'Remaining balance'}:{' '}
                                    <strong className="text-danger">{fmt(calc.remaining)}</strong>
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                <strong>
                                    {language === 'es' ? 'Método de Pago del Depósito' : 'Deposit Payment Method'}
                                </strong>
                            </Form.Label>
                            <Form.Select
                                value={depositPaymentMethod}
                                onChange={(e) => setDepositPaymentMethod(e.target.value)}
                            >
                                <option value="Cash">{language === 'es' ? 'Efectivo' : 'Cash'}</option>
                                <option value="Card">{language === 'es' ? 'Tarjeta de Crédito/Débito' : 'Credit / Debit Card'}</option>
                                <option value="Zelle">Zelle</option>
                            </Form.Select>
                            {depositPaymentMethod === 'Card' && passCcFees && parseFloat(depositAmount) > 0 && (
                                <Form.Text className="text-muted">
                                    {language === 'es'
                                        ? `3% de cargo: ${fmt(parseFloat(depositAmount) * 0.03)} — total: ${fmt(parseFloat(depositAmount) * 1.03)}`
                                        : `3% fee: ${fmt(parseFloat(depositAmount) * 0.03)} — customer pays: ${fmt(parseFloat(depositAmount) * 1.03)}`}
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label><strong>{language === 'es' ? 'Plan de Pago' : 'Payment Plan'}</strong></Form.Label>
                            <Form.Select
                                value={paymentPlan}
                                onChange={(e) => { setPaymentPlan(e.target.value); setInstallmentLocked(false); }}
                            >
                                {PAYMENT_PLANS.map((p) => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-1">
                            <Form.Label>
                                <strong>{language === 'es' ? 'Cuota por Pago' : 'Amount per Payment'}</strong>
                                {' '}
                                {!installmentLocked && calc.payments > 0 && (
                                    <Badge bg="success" className="ms-2" style={{ fontSize: 10 }}>
                                        {language === 'es' ? 'Auto' : 'Auto-calculated'}
                                    </Badge>
                                )}
                                {installmentLocked && (
                                    <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: 10 }}>
                                        {language === 'es' ? 'Manual' : 'Manual override'}
                                    </Badge>
                                )}
                            </Form.Label>
                            <Form.Control
                                type="number" min={0.01} step={0.01}
                                value={installmentAmount}
                                onChange={(e) => { setInstallmentAmount(e.target.value); setInstallmentLocked(true); }}
                                placeholder="0.00"
                            />
                        </Form.Group>

                        {calc.payments > 0 && (
                            <div className="alert alert-info p-2 mt-2 small">
                                <strong>
                                    {calc.payments} {language === 'es' ? 'pago(s) de' : 'payment(s) of'}{' '}
                                    {fmt(installmentLocked ? parseFloat(installmentAmount || 0) : calc.amount)}
                                </strong>
                                {installmentLocked && (
                                    <span
                                        className="ms-3 text-primary"
                                        style={{ cursor: 'pointer', textDecoration: 'underline', fontSize: 11 }}
                                        onClick={() => { setInstallmentLocked(false); setInstallmentAmount(String(calc.amount)); }}
                                    >
                                        {language === 'es' ? '↺ Automático' : '↺ Reset to auto'}
                                    </span>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* ── Step 3: Contact ── */}
                {step === 3 && (
                    <>
                        <h5>{language === 'es' ? 'Información de Contacto' : 'Contact Information'}</h5>
                        <p className="text-muted small">
                            {language === 'es'
                                ? 'Verifique el contacto del cliente. Se enviará un SMS con el enlace del contrato.'
                                : "Verify the customer's contact info. An SMS with the contract link will be sent."}
                        </p>

                        <Form.Group className="mb-3">
                            <Form.Label><strong>{language === 'es' ? 'Teléfono (E.164, ej. +18635551234)' : 'Phone (E.164, e.g. +18635551234)'}</strong></Form.Label>
                            <Form.Control
                                type="tel"
                                value={customerPhone}
                                onChange={(e) => setCustomerPhone(e.target.value)}
                                placeholder="+18635551234"
                            />
                            <Form.Text className="text-muted">
                                {language === 'es' ? 'Deje en blanco para omitir el SMS.' : 'Leave blank to skip SMS.'}
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label><strong>{language === 'es' ? 'Correo Electrónico' : 'Email Address'}</strong></Form.Label>
                            <Form.Control
                                type="email"
                                value={customerEmail}
                                onChange={(e) => setCustomerEmail(e.target.value)}
                                placeholder="customer@email.com"
                            />
                        </Form.Group>

                        <Alert variant="info" className="small mb-0">
                            📱 {language === 'es'
                                ? 'El cliente recibirá un SMS con un enlace seguro para descargar el contrato.'
                                : 'The customer will receive a secure link to download the signed contract.'}
                        </Alert>
                    </>
                )}

                {/* ── Step 4: Contract Preview ── */}
                {step === 4 && (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">{language === 'es' ? 'Vista Previa del Contrato' : 'Contract Preview'}</h5>
                            <Badge bg="info">{language === 'es' ? 'Lea antes de firmar' : 'Read before signing'}</Badge>
                        </div>
                        <p className="text-muted small mb-3">
                            {language === 'es'
                                ? 'Por favor muestre este contrato al cliente para que lo lea antes de firmar.'
                                : 'Please show this contract to the customer to read before signing.'}
                        </p>

                        <ContractPreview
                            lang={language}
                            sale={sale}
                            lineItems={lineItems}
                            depositAmount={depositAmount}
                            installmentAmount={installmentAmount}
                            paymentPlan={paymentPlan}
                            durationDays={durationDays}
                            maturityDate={maturityDate}
                            exchangeDays={exchangeDays}
                            isCustomJewelry={isCustomJewelry}
                            allowOnlinePayments={allowOnlinePayments}
                            passCcFees={passCcFees}
                            employeeName={employeeName}
                            customerPhone={customerPhone}
                            customerEmail={customerEmail}
                        />

                        <Alert variant="warning" className="small mt-3 mb-0">
                            ⚠️ {language === 'es'
                                ? 'Una vez generado, el contrato queda bloqueado. Verifique todos los detalles antes de continuar.'
                                : 'Once generated, the contract is locked and cannot be edited. Verify all details before continuing.'}
                        </Alert>
                    </>
                )}

                {/* ── Step 5: Signature ── */}
                {step === 5 && (
                    <>
                        <h5>{language === 'es' ? 'Firma del Cliente' : 'Customer Signature'}</h5>
                        <p className="text-muted small">
                            {language === 'es'
                                ? 'Pida al cliente que firme con el mouse, lápiz digital o dedo.'
                                : 'Ask the customer to sign below using a mouse, stylus, or finger.'}
                        </p>

                        <div style={{ border: '1px solid #ccc', borderRadius: 6, background: '#fafafa', display: 'inline-block' }}>
                            <canvas
                                ref={canvasRef}
                                width={580}
                                height={160}
                                style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
                                onMouseDown={startDraw}
                                onMouseMove={draw}
                                onMouseUp={stopDraw}
                                onMouseLeave={stopDraw}
                                onTouchStart={startDraw}
                                onTouchMove={draw}
                                onTouchEnd={stopDraw}
                            />
                        </div>

                        <div className="mt-2">
                            <Button variant="outline-secondary" size="sm" onClick={clearCanvas}>
                                {language === 'es' ? 'Borrar Firma' : 'Clear Signature'}
                            </Button>
                        </div>

                        <Alert variant="secondary" className="small mt-3 mb-0">
                            📄 {language === 'es'
                                ? 'Al firmar y hacer clic en "Generar Contrato" se creará un PDF bloqueado y se enviará un SMS al cliente.'
                                : 'Clicking "Generate Contract" creates a locked PDF and sends an SMS to the customer.'}
                        </Alert>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>

                {step > 0 && (
                    <Button variant="outline-primary" onClick={() => setStep((s) => s - 1)} disabled={submitting}>
                        ← {language === 'es' ? 'Atrás' : 'Back'}
                    </Button>
                )}

                {step < STEPS.length - 1 && (
                    <Button variant="primary" onClick={() => setStep((s) => s + 1)} disabled={!stepValid()}>
                        {language === 'es' ? 'Siguiente' : 'Next'} →
                    </Button>
                )}

                {step === STEPS.length - 1 && (
                    <Button variant="success" onClick={handleSubmit} disabled={submitting || !stepValid()}>
                        {submitting
                            ? <><Spinner size="sm" animation="border" className="me-2" />
                              {language === 'es' ? 'Generando…' : 'Generating…'}</>
                            : (language === 'es' ? '✓ Generar Contrato' : '✓ Generate Contract')
                        }
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
